import { useTrackers } from '@ps/web-shared-analytics';
import { useTrackingLabel } from '@ps/web-shared-components';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Search } from '@swf/sports/components/Search/Search';
import { useLayout } from '@swf/sports/providers/LayoutProvider';

import { useSportsMenuWidget } from '../../model';
import { initialSearchResults } from '../../model/defaultState';
import { getSearchResults } from '../../utils/global-search-helpers';

import { TXTCLI_Search } from './SportsGlobalSearch.messages.json';
import styles from './SportsGlobalSearch.scss';
import { SearchResultsContainer } from './components/SearchResults/SearchResults';

export const MIN_CHARACTERS_TO_SEARCH = 2;
export const SEARCH_QUERY_REGEX = /[^a-zA-Z0-9@\-/ ]/g;

export const SportsGlobalSearch: React.FC = () => {
  const { widgetSelect, widgetDispatch } = useSportsMenuWidget();
  const searchLinks = useSelector(widgetSelect.links);
  const isFetchingSearch = useSelector(widgetSelect.isFetchingSearch);
  const searchQuery = useSelector(widgetSelect.searchQuery);
  const { inputFilled } = useTrackers();
  const { showNavigationSection } = useLayout();
  const searchTrackingLabel = useTrackingLabel(TXTCLI_Search);

  const getAndSetSearchResults = useCallback(() => {
    const searchResults = getSearchResults(searchQuery.toLowerCase(), searchLinks);
    widgetDispatch.setSearchResults({ results: searchResults, isNewSearchQuery: false });
  }, [searchLinks, searchQuery, widgetDispatch]);

  const isQueryLengthEnough = searchQuery.length >= MIN_CHARACTERS_TO_SEARCH;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Trim only at the start as some events might be matching by space at the end
    const inputValue = event.target.value.trimStart();
    const formattedInputValue = inputValue.replace(SEARCH_QUERY_REGEX, '');
    widgetDispatch.updateSearchQuery(formattedInputValue);
  };

  const handleSearchClear = () => {
    widgetDispatch.updateSearchQuery('');
  };

  const handleSearchFocus = () => {
    if (!searchQuery) {
      widgetDispatch.searchRequested();
    }
  };

  const handleSearchBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    inputFilled({ fieldType: 'text', fieldName: searchTrackingLabel }, event.target.value);
  };

  useEffect(() => {
    const isMenuClosed = !showNavigationSection;
    if (isMenuClosed) {
      widgetDispatch.updateSearchQuery('');
    }
  }, [showNavigationSection, widgetDispatch]);

  useEffect(() => {
    if (isFetchingSearch) {
      return;
    }
    if (!isQueryLengthEnough) {
      widgetDispatch.setSelectedEventType(undefined);
      widgetDispatch.setSearchResults({ results: initialSearchResults, isNewSearchQuery: true });
      return;
    }
    getAndSetSearchResults();
  }, [widgetDispatch, isFetchingSearch, getAndSetSearchResults, isQueryLengthEnough, searchQuery]);

  return (
    <div className={styles.container} role="search">
      <Search
        searchTerm={searchQuery}
        onChange={handleSearchChange}
        onClear={handleSearchClear}
        placeholderTranslation={TXTCLI_Search}
        trackingLabel={searchTrackingLabel}
        onFocus={handleSearchFocus}
        onBlur={handleSearchBlur}
      />
      {isQueryLengthEnough && <SearchResultsContainer />}
    </div>
  );
};
